import { useState, useEffect, useRef } from "react";
import { useParams, useLocation } from "wouter";
import { useQuery, useMutation } from "@tanstack/react-query";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import {
  Loader2, Trophy, Users, Share2, Copy, ArrowLeft, ChevronDown, ChevronUp,
  Download, Lock, Unlock, ChevronRight, Star, Crown, Zap
} from "lucide-react";
import { useAuth } from "@/hooks/use-auth";
import { WC_TEAMS, WC_GROUPS, getTeamsByGroup, getMatchesByGroup, WILDCARD_TYPES, WC_BRACKET_SLOTS, KNOCKOUT_ROUNDS, getFlagUrl, BASE_POINTS } from "@shared/worldcup-data-client";
import type { BracketSlot } from "@shared/worldcup-data-client";
import type { WCTeam } from "@shared/worldcup-data-client";

const selectStyle: React.CSSProperties = {
  width:"100%", background:"rgba(255,255,255,0.04)", border:`1px solid #243225`,
  borderRadius:10, padding:"10px 14px", fontSize:13, color:"#f0ebe0", outline:"none",
  fontFamily:"'Outfit',system-ui,sans-serif", boxSizing:"border-box",
  appearance:"none", WebkitAppearance:"none", cursor:"pointer",
  backgroundImage:`url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%236a7a5a' stroke-width='2'%3E%3Cpath d='M6 9l6 6 6-6'/%3E%3C/svg%3E")`,
  backgroundRepeat:"no-repeat", backgroundPosition:"right 12px center", paddingRight:36,
};

function WildcardInput({ wt, value, onChange }: {
  wt: { id: string; label: string; points: number };
  value: string;
  onChange: (v: string) => void;
}) {
  if (wt.id === "tournament_winner") {
    const teams = WC_TEAMS.filter((t) => !t.id.endsWith("2")).sort((a, b) => a.fifaRank - b.fifaRank);
    return (
      <select data-testid={`input-wildcard-${wt.id}`} value={value} onChange={(e) => onChange(e.target.value)} style={selectStyle}>
        <option value="">— Pick the winner —</option>
        {teams.map((t) => (
          <option key={t.id} value={t.name}>{t.flag} {t.name} (ranked #{t.fifaRank})</option>
        ))}
      </select>
    );
  }

  if (wt.id === "dark_horse_qf") {
    const darkHorses = WC_TEAMS.filter((t) => t.fifaRank > 20 && !t.id.endsWith("2")).sort((a, b) => a.fifaRank - b.fifaRank);
    return (
      <select data-testid={`input-wildcard-${wt.id}`} value={value} onChange={(e) => onChange(e.target.value)} style={selectStyle}>
        <option value="">— Pick a dark horse (ranked 21+) —</option>
        {darkHorses.map((t) => (
          <option key={t.id} value={t.name}>{t.flag} {t.name} (ranked #{t.fifaRank})</option>
        ))}
      </select>
    );
  }

  if (wt.id === "total_goals_final") {
    return (
      <select data-testid={`input-wildcard-${wt.id}`} value={value} onChange={(e) => onChange(e.target.value)} style={selectStyle}>
        <option value="">— Pick total goals in the final —</option>
        {[0,1,2,3,4,5,6,7,8,9].map((n) => (
          <option key={n} value={String(n)}>{n} goal{n === 1 ? "" : "s"}</option>
        ))}
        <option value="10+">10+ goals</option>
      </select>
    );
  }

  return (
    <input
      data-testid={`input-wildcard-${wt.id}`}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={wt.id === "golden_boot" ? "e.g. Kylian Mbappé" : "e.g. 2-1"}
      style={{ width:"100%", background:"rgba(255,255,255,0.04)", border:"1px solid #243225", borderRadius:10, padding:"10px 14px", fontSize:13, color:"#f0ebe0", outline:"none", fontFamily:"'Outfit',system-ui,sans-serif", boxSizing:"border-box" }}
    />
  );
}

function FlagImg({ cc, size = 20 }: { cc: string; size?: number }) {
  const h = Math.round(size * 0.75);
  return (
    <img
      src={getFlagUrl(cc)}
      width={size}
      height={h}
      alt=""
      style={{ display:"inline-block", verticalAlign:"middle", borderRadius:2, flexShrink:0 }}
      onError={(e) => { (e.target as HTMLImageElement).style.display = "none"; }}
    />
  );
}

const C = {
  bg:"#080e0a", card:"#0f1810", cardAlt:"#141f15", border:"#243225",
  gold:"#d4a017", goldDim:"rgba(212,160,23,0.12)", goldGlow:"rgba(212,160,23,0.25)",
  green:"#4ade80", greenDim:"rgba(74,222,128,0.10)", greenMid:"rgba(74,222,128,0.20)",
  cream:"#f0ebe0", text:"#ddd5c0", muted:"#6a7a5a", faint:"#1a2a1a",
  red:"#f87171",
};

const inputStyle: React.CSSProperties = {
  width:"100%", background:"rgba(255,255,255,0.04)", border:`1px solid ${C.border}`,
  borderRadius:10, padding:"10px 14px", fontSize:13, color:C.cream, outline:"none",
  fontFamily:"'Outfit',system-ui,sans-serif", boxSizing:"border-box",
};

function GroupPicksWizard({ poolId, socialCtx }: { poolId: number; socialCtx?: { linkedinConnected?: boolean; facebookConnected?: boolean; workspaceId?: number | null } }) {
  const [activeGroup, setActiveGroup] = useState("A");
  const [picks, setPicks] = useState<Record<string, "H" | "D" | "A">>({});
  const [standings, setStandings] = useState<Record<string, any[]>>({});
  const [wildcardPicks, setWildcardPicks] = useState<Record<string, string>>({});
  const [step, setStep] = useState<"groups" | "wildcards" | "done">("groups");
  const { toast } = useToast();

  const { data: savedPicks = [] } = useQuery<any[]>({
    queryKey: ["/api/pools", poolId, "group-picks"],
    queryFn: () => fetch(`/api/pools/${poolId}/group-picks`, { credentials: "include" }).then((r) => r.json()),
  });

  const { data: savedWildcards = [] } = useQuery<any[]>({
    queryKey: ["/api/pools", poolId, "wildcard-picks"],
    queryFn: () => fetch(`/api/pools/${poolId}/wildcard-picks`, { credentials: "include" }).then((r) => r.json()),
  });

  useEffect(() => {
    if (savedPicks.length > 0) {
      const p: Record<string, "H" | "D" | "A"> = {};
      for (const pick of savedPicks) p[pick.matchId] = pick.predictedResult;
      setPicks(p);

      // Re-simulate standings for every group that has at least one saved pick
      const groupsWithPicks = [...new Set(savedPicks.map((pk: any) => pk.groupId as string))];
      for (const groupId of groupsWithPicks) {
        const groupPicks: Record<string, string> = {};
        for (const pk of savedPicks) {
          if (pk.groupId === groupId) groupPicks[pk.matchId] = pk.predictedResult;
        }
        fetch("/api/pools/simulate-group", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ groupId, results: groupPicks }),
          credentials: "include",
        })
          .then((r) => r.json())
          .then((data) => setStandings((prev) => ({ ...prev, [groupId]: data.standings })))
          .catch(() => {});
      }
    }
  }, [savedPicks]);

  useEffect(() => {
    if (savedWildcards.length > 0) {
      const w: Record<string, string> = {};
      for (const wc of savedWildcards) w[wc.pickType] = wc.value;
      setWildcardPicks(w);
    }
  }, [savedWildcards]);

  const simulateMutation = useMutation({
    mutationFn: async ({ groupId, results }: { groupId: string; results: Record<string, string> }) => {
      const r = await fetch("/api/pools/simulate-group", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ groupId, results }),
        credentials: "include",
      });
      return r.json();
    },
    onSuccess: (data, vars) => {
      setStandings((prev) => ({ ...prev, [vars.groupId]: data.standings }));
    },
  });

  function applyPick(matchId: string, groupId: string, result: "H" | "D" | "A") {
    const newPicks = { ...picks, [matchId]: result };
    setPicks(newPicks);
    const groupPicks: Record<string, "H" | "D" | "A"> = {};
    const matches = getMatchesByGroup(groupId);
    for (const m of matches) if (newPicks[m.id]) groupPicks[m.id] = newPicks[m.id];
    simulateMutation.mutate({ groupId, results: groupPicks });
  }

  const saveMutation = useMutation({
    mutationFn: async () => {
      const picksArr = Object.entries(picks).map(([matchId, predictedResult]) => {
        const allMatches = (WC_GROUPS as readonly string[]).flatMap((g) => getMatchesByGroup(g));
        const match = allMatches.find((m) => m.id === matchId);
        return { groupId: match?.groupId || matchId.split("-")[0], matchId, predictedResult };
      });
      const r = await apiRequest("POST", `/api/pools/${poolId}/group-picks`, { picks: picksArr });
      if (!r.ok) throw new Error("Failed to save");
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/pools", poolId, "group-picks"] });
      toast({ title: "Group picks saved!" });
    },
    onError: (e: any) => toast({ title: e.message, variant: "destructive" }),
  });

  const saveWildcardsMutation = useMutation({
    mutationFn: async () => {
      const picksArr = Object.entries(wildcardPicks).map(([pickType, value]) => ({ pickType, value }));
      const r = await apiRequest("POST", `/api/pools/${poolId}/wildcard-picks`, { picks: picksArr });
      if (!r.ok) throw new Error("Failed to save wildcards");
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/pools", poolId, "wildcard-picks"] });
      toast({ title: "All picks saved!" });
      setStep("done");
    },
    onError: (e: any) => toast({ title: e.message, variant: "destructive" }),
  });

  const teams = getTeamsByGroup(activeGroup);
  const matches = getMatchesByGroup(activeGroup);
  const currentStandings = standings[activeGroup];

  const groupsComplete = (WC_GROUPS as readonly string[]).filter((g) => {
    const gMatches = getMatchesByGroup(g);
    return gMatches.every((m) => picks[m.id]);
  }).length;

  if (step === "done") {
    return (
      <div style={{ textAlign:"center", padding:"32px 16px" }}>
        <div style={{ width:60, height:60, borderRadius:"50%", background:C.goldDim, border:`1px solid rgba(212,160,23,0.3)`, display:"flex", alignItems:"center", justifyContent:"center", margin:"0 auto 16px", fontSize:28 }}>🏆</div>
        <h3 style={{ fontSize:18, fontWeight:800, color:C.cream, marginBottom:8 }}>Picks Submitted!</h3>
        <p style={{ fontSize:12, color:C.muted, marginBottom:24, lineHeight:1.6 }}>Locked in for tournament kickoff on <strong style={{ color:C.gold }}>June 11, 2026</strong>. Invite more friends to build your pool.</p>

        <div style={{ background:C.cardAlt, border:`1px solid ${C.border}`, borderTop:`2px solid ${C.gold}`, borderRadius:12, padding:16, marginBottom:20, textAlign:"left", maxWidth:380, margin:"0 auto 20px" }}>
          <p style={{ fontSize:9, fontWeight:700, color:C.gold, textTransform:"uppercase", letterSpacing:"0.15em", marginBottom:8 }}>Share with your audience</p>
          {socialCtx?.linkedinConnected ? (
            <>
              <p style={{ fontSize:11, color:C.muted, marginBottom:12, lineHeight:1.6 }}>LinkedIn is connected. Peggy will draft a post about your picks — one click, ready to publish.</p>
              <button style={{ width:"100%", padding:"10px 0", borderRadius:8, background:"#0077b5", border:"none", color:"#fff", fontWeight:700, fontSize:13, cursor:"pointer", fontFamily:"inherit" }}
                onClick={async () => {
                  try {
                    const res = await fetch(`/api/pools/${poolId}/peggy-draft`, { method: "POST" });
                    if (res.ok) { const data = await res.json(); if (data.redirectUrl) window.open(data.redirectUrl, "_blank"); }
                  } catch { /* ignore */ }
                }}
                data-testid="button-peggy-draft-post">
                Draft LinkedIn Post with Peggy
              </button>
            </>
          ) : socialCtx?.facebookConnected ? (
            <>
              <p style={{ fontSize:11, color:C.muted, marginBottom:12, lineHeight:1.6 }}>Facebook is connected. Peggy will draft a post about your picks — one click, ready to publish.</p>
              <button style={{ width:"100%", padding:"10px 0", borderRadius:8, background:"#1877f2", border:"none", color:"#fff", fontWeight:700, fontSize:13, cursor:"pointer", fontFamily:"inherit" }}
                onClick={async () => {
                  try {
                    const res = await fetch(`/api/pools/${poolId}/peggy-draft`, { method: "POST" });
                    if (res.ok) { const data = await res.json(); if (data.redirectUrl) window.open(data.redirectUrl, "_blank"); }
                  } catch { /* ignore */ }
                }}
                data-testid="button-peggy-draft-post-fb">
                Draft Facebook Post with Peggy
              </button>
            </>
          ) : (
            <>
              <p style={{ fontSize:11, color:C.muted, marginBottom:12, lineHeight:1.6 }}>Connect LinkedIn or Facebook in your workspace to let Peggy draft 3 post ideas about your picks.</p>
              <button style={{ width:"100%", padding:"10px 0", borderRadius:8, background:"transparent", border:`1px solid ${C.border}`, color:C.muted, fontWeight:600, fontSize:13, cursor:"pointer", fontFamily:"inherit" }}
                onClick={() => { if (socialCtx?.workspaceId) window.open(`/workspace/${socialCtx.workspaceId}/settings`, "_blank"); }}
                data-testid="button-connect-social-for-peggy">
                Connect Social to Draft with Peggy
              </button>
            </>
          )}
        </div>

        <div style={{ display:"flex", gap:10, justifyContent:"center" }}>
          <button onClick={() => setStep("groups")} style={{ padding:"9px 16px", borderRadius:8, background:"transparent", border:`1px solid ${C.border}`, color:C.muted, fontSize:12, fontWeight:600, cursor:"pointer", fontFamily:"inherit" }}>Edit Group Picks</button>
          <button onClick={() => setStep("wildcards")} style={{ padding:"9px 16px", borderRadius:8, background:"transparent", border:`1px solid ${C.border}`, color:C.muted, fontSize:12, fontWeight:600, cursor:"pointer", fontFamily:"inherit" }}>Edit Wildcards</button>
        </div>
      </div>
    );
  }

  if (step === "wildcards") {
    const filledWildcards = Object.keys(wildcardPicks).filter((k) => wildcardPicks[k]?.trim()).length;
    return (
      <div style={{ display:"flex", flexDirection:"column", gap:14 }}>
        <div style={{ display:"flex", alignItems:"center", gap:10 }}>
          <button onClick={() => setStep("groups")} style={{ display:"flex", alignItems:"center", gap:4, fontSize:11, color:C.muted, background:"none", border:"none", cursor:"pointer", fontFamily:"inherit", padding:0 }}>
            <ArrowLeft size={13} /> Back to Groups
          </button>
          <span style={{ color:C.border }}>·</span>
          <span style={{ fontSize:11, color:C.muted }}>{filledWildcards}/5 filled</span>
        </div>
        <div>
          <h3 style={{ fontSize:15, fontWeight:800, color:C.cream, marginBottom:4 }}>Wildcard Picks</h3>
          <p style={{ fontSize:11, color:C.muted, lineHeight:1.6 }}>Bonus predictions locked at kickoff. Points awarded after tournament results.</p>
        </div>
        <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
          {(WILDCARD_TYPES as readonly typeof WILDCARD_TYPES[number][]).map((wt) => (
            <div key={wt.id} style={{ background:C.cardAlt, border:`1px solid ${C.border}`, borderRadius:10, padding:14 }}>
              <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:10 }}>
                <p style={{ fontSize:13, fontWeight:700, color:C.cream, margin:0 }}>{wt.label}</p>
                {wt.points > 0
                  ? <span style={{ fontSize:10, background:C.goldDim, border:"1px solid rgba(212,160,23,0.3)", borderRadius:6, padding:"2px 8px", color:C.gold, fontWeight:700 }}>+{wt.points} pts</span>
                  : <span style={{ fontSize:10, background:"rgba(255,255,255,0.05)", borderRadius:6, padding:"2px 8px", color:C.muted }}>Tiebreaker</span>
                }
              </div>
              <WildcardInput
                wt={wt}
                value={wildcardPicks[wt.id] || ""}
                onChange={(v) => setWildcardPicks((prev) => ({ ...prev, [wt.id]: v }))}
              />
            </div>
          ))}
        </div>
        <button data-testid="button-save-wildcards" onClick={() => saveWildcardsMutation.mutate()} disabled={saveWildcardsMutation.isPending}
          style={{ padding:"12px 0", borderRadius:10, background:`linear-gradient(135deg,${C.gold},#c8860d)`, border:"none", color:"#080e0a", fontWeight:800, fontSize:14, cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center", gap:8, fontFamily:"inherit" }}>
          {saveWildcardsMutation.isPending ? <Loader2 size={14} className="animate-spin" /> : null}
          Save All Picks
        </button>
        <p style={{ fontSize:10, color:C.muted, textAlign:"center", opacity:0.7 }}>Leaving fields blank is fine — unfilled wildcards score 0.</p>
      </div>
    );
  }

  return (
    <div style={{ display:"flex", flexDirection:"column", gap:14 }}>
      {/* Header row: instruction + Save button */}
      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", gap:8 }}>
        <p style={{ fontSize:12, color:C.muted, lineHeight:1.5, margin:0 }}>Pick every match — 12 groups, 6 matches each</p>
        <button data-testid="button-save-group-picks" onClick={() => saveMutation.mutate()} disabled={saveMutation.isPending}
          style={{ padding:"9px 16px", borderRadius:9, background:"rgba(255,255,255,0.06)", border:`1px solid ${C.border}`, color:C.text, fontSize:12, fontWeight:700, cursor:"pointer", display:"flex", alignItems:"center", gap:6, fontFamily:"inherit", flexShrink:0, whiteSpace:"nowrap" }}>
          {saveMutation.isPending ? <Loader2 size={13} className="animate-spin" /> : null}
          Save Progress
        </button>
      </div>

      {/* Group tab strip — single scrollable row, no wrapping */}
      <div>
        <div style={{ display:"flex", gap:6, overflowX:"auto", paddingBottom:6, WebkitOverflowScrolling:"touch", scrollbarWidth:"none" }}>
          {(WC_GROUPS as readonly string[]).map((g) => {
            const done = getMatchesByGroup(g).every((m) => picks[m.id]);
            const isActive = activeGroup === g;
            return (
              <button key={g} data-testid={`tab-group-${g}`} onClick={() => setActiveGroup(g)}
                style={{ minWidth:38, height:38, borderRadius:9, fontSize:13, fontWeight:800, border:"none", cursor:"pointer", fontFamily:"inherit", transition:"all 0.15s", flexShrink:0,
                  background: isActive ? `linear-gradient(135deg,${C.gold},#c8860d)` : done ? C.greenDim : "rgba(255,255,255,0.06)",
                  color: isActive ? "#080e0a" : done ? C.green : C.muted,
                  outline: isActive ? `2px solid ${C.gold}` : done ? `1px solid rgba(74,222,128,0.3)` : "none",
                }}>
                {g}
              </button>
            );
          })}
        </div>
        <p style={{ fontSize:11, color:C.muted, marginTop:2, opacity:0.7 }}>{groupsComplete}/12 groups complete</p>
      </div>

      <div style={{ background:C.card, border:`1px solid ${C.border}`, borderTop:`2px solid ${C.gold}`, borderRadius:12, overflow:"hidden" }}>
        <div style={{ padding:"12px 16px", borderBottom:`1px solid ${C.border}`, display:"flex", alignItems:"center", justifyContent:"space-between" }}>
          <div>
            <h3 style={{ fontSize:14, fontWeight:800, color:C.cream, margin:0 }}>Group {activeGroup}</h3>
            <div style={{ display:"flex", gap:10, marginTop:4, flexWrap:"wrap" }}>
              {teams.map((t) => (
                <span key={t.id} style={{ fontSize:10, color:C.muted, display:"inline-flex", alignItems:"center", gap:4 }}><FlagImg cc={t.cc} size={14} /> {t.name}</span>
              ))}
            </div>
          </div>
        </div>

        <div style={{ borderBottom:`1px solid ${C.border}` }}>
          {matches.map((match) => {
            const home = WC_TEAMS.find((t) => t.id === match.homeTeamId);
            const away = WC_TEAMS.find((t) => t.id === match.awayTeamId);
            const picked = picks[match.id];
            return (
              <div key={match.id} style={{ display:"flex", alignItems:"center", gap:6, padding:"13px 14px", borderBottom:`1px solid rgba(36,50,37,0.5)` }}>
                {/* Home team */}
                <span style={{ fontSize:13, color:picked === "H" ? C.green : C.text, fontWeight: picked === "H" ? 700 : 400, flex:1, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap", display:"flex", alignItems:"center", gap:5 }}>
                  {home && <FlagImg cc={home.cc} size={18} />}
                  <span style={{ overflow:"hidden", textOverflow:"ellipsis" }}>{home?.name}</span>
                </span>
                {/* W / D / W buttons */}
                <div style={{ display:"flex", gap:5, flexShrink:0 }}>
                  {(["H","D","A"] as const).map((val) => {
                    const isActive = picked === val;
                    const label = val === "D" ? "D" : "W";
                    const activeColor = val === "H" ? C.green : val === "D" ? C.gold : C.red;
                    return (
                      <button key={val} data-testid={`pick-${match.id}-${val}`} onClick={() => applyPick(match.id, match.groupId, val)}
                        title={val === "H" ? `${home?.name} wins` : val === "D" ? "Draw" : `${away?.name} wins`}
                        style={{ width:38, height:36, fontSize:12, fontWeight:800, borderRadius:8, border:`1px solid ${isActive ? activeColor : C.border}`, cursor:"pointer", fontFamily:"inherit", transition:"all 0.12s", display:"flex", alignItems:"center", justifyContent:"center",
                          background: isActive ? activeColor : "rgba(255,255,255,0.04)",
                          color: isActive ? (val === "D" ? "#080e0a" : "#fff") : C.muted,
                        }}>
                        {label}
                      </button>
                    );
                  })}
                </div>
                {/* Away team */}
                <span style={{ fontSize:13, color:picked === "A" ? C.green : C.text, fontWeight: picked === "A" ? 700 : 400, flex:1, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap", textAlign:"right", display:"flex", alignItems:"center", justifyContent:"flex-end", gap:5 }}>
                  <span style={{ overflow:"hidden", textOverflow:"ellipsis" }}>{away?.name}</span>
                  {away && <FlagImg cc={away.cc} size={18} />}
                </span>
              </div>
            );
          })}
        </div>

        {currentStandings && (
          <div style={{ padding:"12px 16px" }}>
            <p style={{ fontSize:9, color:C.muted, textTransform:"uppercase", letterSpacing:"0.12em", fontWeight:700, marginBottom:8 }}>Predicted Standings</p>
            <div style={{ display:"flex", flexDirection:"column", gap:5 }}>
              {currentStandings.map((s: any, i: number) => {
                const team = WC_TEAMS.find((t) => t.id === s.teamId);
                const isTop2 = i < 2;
                const is3rd = i === 2;
                return (
                  <div key={s.teamId} style={{ display:"flex", alignItems:"center", gap:8, padding:"6px 10px", borderRadius:8, border:`1px solid ${isTop2 ? "rgba(74,222,128,0.25)" : is3rd ? "rgba(212,160,23,0.2)" : C.border}`, background: isTop2 ? C.greenDim : is3rd ? C.goldDim : "rgba(255,255,255,0.02)" }}>
                    <span style={{ fontSize:10, fontWeight:800, width:16, textAlign:"center", color: isTop2 ? C.green : is3rd ? C.gold : C.muted }}>{i + 1}</span>
                    <span style={{ fontSize:11, flex:1, color: isTop2 ? C.cream : C.text, display:"flex", alignItems:"center", gap:5 }}>{team && <FlagImg cc={team.cc} size={16} />}{team?.name}</span>
                    <span style={{ fontSize:10, fontWeight:700, color: isTop2 ? C.green : C.text }}>{s.points}pt</span>
                    <span style={{ fontSize:10, color:C.muted }}>{s.gd > 0 ? "+" : ""}{s.gd} GD</span>
                  </div>
                );
              })}
            </div>
            <p style={{ fontSize:9, color:C.muted, marginTop:8, opacity:0.7 }}>Green = advances · Gold = best 3rd place contender</p>
          </div>
        )}
      </div>

      <div style={{ display:"flex", justifyContent:"space-between", gap:10 }}>
        {activeGroup !== "A" && (
          <button onClick={() => setActiveGroup(String.fromCharCode(activeGroup.charCodeAt(0) - 1))}
            style={{ display:"flex", alignItems:"center", gap:6, padding:"9px 14px", borderRadius:8, background:"transparent", border:`1px solid ${C.border}`, color:C.muted, fontSize:12, fontWeight:600, cursor:"pointer", fontFamily:"inherit" }}>
            <ArrowLeft size={13} /> Prev Group
          </button>
        )}
        <div style={{ marginLeft:"auto", display:"flex", gap:8 }}>
          {activeGroup !== "L" ? (
            <button onClick={() => setActiveGroup(String.fromCharCode(activeGroup.charCodeAt(0) + 1))}
              style={{ display:"flex", alignItems:"center", gap:6, padding:"9px 14px", borderRadius:8, background:"rgba(255,255,255,0.06)", border:`1px solid ${C.border}`, color:C.text, fontSize:12, fontWeight:700, cursor:"pointer", fontFamily:"inherit" }}>
              Next Group <ChevronRight size={13} />
            </button>
          ) : (
            <button data-testid="button-go-to-wildcards"
              onClick={async () => { await saveMutation.mutateAsync().catch(() => {}); setStep("wildcards"); }}
              disabled={saveMutation.isPending}
              style={{ display:"flex", alignItems:"center", gap:6, padding:"10px 18px", borderRadius:8, background:`linear-gradient(135deg,${C.gold},#c8860d)`, border:"none", color:"#080e0a", fontSize:13, fontWeight:800, cursor:"pointer", fontFamily:"inherit" }}>
              {saveMutation.isPending ? <Loader2 size={14} className="animate-spin" /> : null}
              Next: Wildcard Picks <ChevronRight size={13} />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Bonus Picks Panel ────────────────────────────────────────────────────────

function BonusPicksPanel({ poolId, round, matchSlot, homeTeam, awayTeam }: {
  poolId: number;
  round: string;
  matchSlot: string;
  homeTeam: WCTeam | null;
  awayTeam: WCTeam | null;
}) {
  const [open, setOpen] = useState(false);
  const [cleanSheet, setCleanSheet] = useState<"" | "yes" | "no">("");
  const [penalties, setPenalties] = useState<"" | "yes" | "no">("");
  const [exactHome, setExactHome] = useState("");
  const [exactAway, setExactAway] = useState("");
  const { toast } = useToast();

  const { data: bonusData } = useQuery<{
    picks: any[];
    isLocked: boolean;
    pointValues: { clean_sheet: number; exact_score: number; penalties: number };
    earnedPoints: Record<string, number> | null;
  }>({
    queryKey: ["/api/pools", poolId, "bonus-picks", round, matchSlot],
    queryFn: () => fetch(`/api/pools/${poolId}/bonus-picks/${round}/${matchSlot}`, { credentials: "include" }).then((r) => r.json()),
    enabled: open,
  });

  useEffect(() => {
    if (!bonusData?.picks) return;
    for (const p of bonusData.picks) {
      if (p.pickType === "clean_sheet") setCleanSheet(p.pickValue as "yes" | "no");
      if (p.pickType === "penalties") setPenalties(p.pickValue as "yes" | "no");
      if (p.pickType === "exact_score") {
        const [h, a] = p.pickValue.split("-");
        setExactHome(h ?? "");
        setExactAway(a ?? "");
      }
    }
  }, [bonusData]);

  const exactScoreValue = exactHome !== "" && exactAway !== "" ? `${exactHome}-${exactAway}` : "";

  const saveMutation = useMutation({
    mutationFn: async () => {
      const picks: any[] = [];
      if (cleanSheet) picks.push({ pickType: "clean_sheet", pickValue: cleanSheet });
      if (penalties) picks.push({ pickType: "penalties", pickValue: penalties });
      if (exactScoreValue) picks.push({ pickType: "exact_score", pickValue: exactScoreValue });
      const r = await apiRequest("POST", `/api/pools/${poolId}/bonus-picks/${round}/${matchSlot}`, { picks });
      if (!r.ok) throw new Error("Failed to save bonus picks");
      return r.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/pools", poolId, "bonus-picks", round, matchSlot] });
      toast({ title: "Bonus picks saved!" });
    },
    onError: (e: any) => toast({ title: e.message, variant: "destructive" }),
  });

  const isLocked = bonusData?.isLocked ?? false;
  const pv = bonusData?.pointValues;
  const ep = bonusData?.earnedPoints;
  const hasSavedPicks = (bonusData?.picks?.length ?? 0) > 0;
  const totalEarned = ep ? Object.values(ep).reduce((s, v) => s + v, 0) : null;

  return (
    <div style={{ borderTop:`1px solid ${C.border}` }}>
      <button
        data-testid={`button-bonus-picks-toggle-${matchSlot}`}
        onClick={() => setOpen((o) => !o)}
        style={{ width:"100%", display:"flex", alignItems:"center", justifyContent:"space-between", padding:"8px 14px", background:"rgba(212,160,23,0.04)", border:"none", cursor:"pointer", fontFamily:"inherit" }}
      >
        <span style={{ fontSize:10, fontWeight:700, color:C.gold, display:"flex", alignItems:"center", gap:6 }}>
          <Star size={11} />
          Bonus Picks
          {hasSavedPicks && <span style={{ background:"rgba(212,160,23,0.25)", borderRadius:10, padding:"1px 6px", fontSize:9, color:C.gold }}>{bonusData?.picks?.length}</span>}
          {isLocked && <Lock size={10} style={{ color:C.muted }} />}
          {totalEarned !== null && <span data-testid={`bonus-total-earned-${matchSlot}`} style={{ background:"rgba(74,222,128,0.2)", borderRadius:10, padding:"1px 6px", fontSize:9, color:C.green, fontWeight:800 }}>+{totalEarned} pts</span>}
        </span>
        {open ? <ChevronUp size={12} style={{ color:C.muted }} /> : <ChevronDown size={12} style={{ color:C.muted }} />}
      </button>

      {open && (
        <div style={{ padding:"12px 14px", display:"flex", flexDirection:"column", gap:10, background:"rgba(212,160,23,0.03)" }}>
          {isLocked ? (
            <>
              {hasSavedPicks ? (
                <div style={{ display:"flex", flexDirection:"column", gap:6 }}>
                  {bonusData?.picks.map((p: any) => {
                    const earned = ep?.[p.pickType];
                    const label = p.pickType === "clean_sheet" ? "Clean Sheet" : p.pickType === "penalties" ? "Goes to Penalties" : "Exact Score";
                    return (
                      <div key={p.pickType} data-testid={`bonus-pick-result-${matchSlot}-${p.pickType}`}
                        style={{ display:"flex", alignItems:"center", justifyContent:"space-between", padding:"6px 10px", borderRadius:8, background:"rgba(255,255,255,0.03)", border:`1px solid ${C.border}` }}>
                        <span style={{ fontSize:11, color:C.text }}>{label}: <strong style={{ color:C.gold }}>{p.pickValue}</strong></span>
                        {earned !== undefined ? (
                          <span style={{ fontSize:11, fontWeight:800, color: earned > 0 ? C.green : C.muted }}>
                            {earned > 0 ? `+${earned} pts` : "0 pts"}
                          </span>
                        ) : (
                          <span style={{ fontSize:10, color:C.muted }}>pending score</span>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p style={{ fontSize:11, color:C.muted, margin:0 }}>No bonus picks saved for this match.</p>
              )}
            </>
          ) : (
            <>
              {/* Clean Sheet */}
              <div>
                <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:6 }}>
                  <label style={{ fontSize:11, fontWeight:700, color:C.text }}>Clean Sheet (either team scores 0)?</label>
                  {pv && <span style={{ fontSize:9, color:C.gold, fontWeight:700 }}>+{pv.clean_sheet} pts</span>}
                </div>
                <div style={{ display:"flex", gap:6 }}>
                  {(["yes", "no"] as const).map((v) => (
                    <button key={v} data-testid={`bonus-clean-sheet-${matchSlot}-${v}`}
                      onClick={() => setCleanSheet(cleanSheet === v ? "" : v)}
                      style={{ flex:1, padding:"7px 0", borderRadius:8, fontSize:11, fontWeight:700, cursor:"pointer", fontFamily:"inherit",
                        background: cleanSheet === v ? (v === "yes" ? C.greenDim : "rgba(248,113,113,0.12)") : "rgba(255,255,255,0.03)",
                        border: `1px solid ${cleanSheet === v ? (v === "yes" ? "rgba(74,222,128,0.4)" : "rgba(248,113,113,0.4)") : C.border}`,
                        color: cleanSheet === v ? (v === "yes" ? C.green : C.red) : C.muted,
                      }}>
                      {v === "yes" ? "Yes" : "No"}
                    </button>
                  ))}
                </div>
              </div>

              {/* Penalties */}
              <div>
                <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:6 }}>
                  <label style={{ fontSize:11, fontWeight:700, color:C.text }}>Goes to Penalties?</label>
                  {pv && <span style={{ fontSize:9, color:C.gold, fontWeight:700 }}>+{pv.penalties} pts</span>}
                </div>
                <div style={{ display:"flex", gap:6 }}>
                  {(["yes", "no"] as const).map((v) => (
                    <button key={v} data-testid={`bonus-penalties-${matchSlot}-${v}`}
                      onClick={() => setPenalties(penalties === v ? "" : v)}
                      style={{ flex:1, padding:"7px 0", borderRadius:8, fontSize:11, fontWeight:700, cursor:"pointer", fontFamily:"inherit",
                        background: penalties === v ? (v === "yes" ? C.greenDim : "rgba(248,113,113,0.12)") : "rgba(255,255,255,0.03)",
                        border: `1px solid ${penalties === v ? (v === "yes" ? "rgba(74,222,128,0.4)" : "rgba(248,113,113,0.4)") : C.border}`,
                        color: penalties === v ? (v === "yes" ? C.green : C.red) : C.muted,
                      }}>
                      {v === "yes" ? "Yes" : "No"}
                    </button>
                  ))}
                </div>
              </div>

              {/* Exact Score — two separate numeric inputs */}
              <div>
                <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:6 }}>
                  <label style={{ fontSize:11, fontWeight:700, color:C.text }}>Exact Scoreline?</label>
                  {pv && <span style={{ fontSize:9, color:C.gold, fontWeight:700 }}>+{pv.exact_score} pts</span>}
                </div>
                <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                  <input
                    data-testid={`bonus-exact-score-home-${matchSlot}`}
                    type="number"
                    min={0}
                    max={99}
                    value={exactHome}
                    onChange={(e) => { const v = e.target.value; if (v === "" || /^\d{1,2}$/.test(v)) setExactHome(v); }}
                    placeholder={homeTeam?.name.slice(0,3).toUpperCase() ?? "H"}
                    style={{ ...inputStyle, fontSize:14, padding:"8px 0", textAlign:"center", flex:1, letterSpacing:"0.04em" }}
                  />
                  <span style={{ fontSize:16, fontWeight:800, color:C.muted, flexShrink:0 }}>–</span>
                  <input
                    data-testid={`bonus-exact-score-away-${matchSlot}`}
                    type="number"
                    min={0}
                    max={99}
                    value={exactAway}
                    onChange={(e) => { const v = e.target.value; if (v === "" || /^\d{1,2}$/.test(v)) setExactAway(v); }}
                    placeholder={awayTeam?.name.slice(0,3).toUpperCase() ?? "A"}
                    style={{ ...inputStyle, fontSize:14, padding:"8px 0", textAlign:"center", flex:1, letterSpacing:"0.04em" }}
                  />
                </div>
              </div>

              <button
                data-testid={`button-save-bonus-picks-${matchSlot}`}
                onClick={() => saveMutation.mutate()}
                disabled={saveMutation.isPending}
                style={{ padding:"9px 0", borderRadius:8, background:`linear-gradient(135deg,${C.gold},#c8860d)`, border:"none", color:"#080e0a", fontWeight:800, fontSize:12, cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center", gap:6, fontFamily:"inherit" }}>
                {saveMutation.isPending ? <Loader2 size={11} className="animate-spin" /> : <Star size={11} />}
                Save Bonus Picks
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}

function BracketSlotRow({ slot, poolId, bracketPicks, setBracketPicks, activeSlot, setActiveSlot }: {
  slot: any;
  poolId: number;
  bracketPicks: Record<string, string>;
  setBracketPicks: (fn: (prev: Record<string, string>) => Record<string, string>) => void;
  activeSlot: string | null;
  setActiveSlot: (s: string | null) => void;
}) {
  const picked = bracketPicks[slot.slot];
  const homeTeam = slot.homeTeam as WCTeam | null;
  const awayTeam = slot.awayTeam as WCTeam | null;
  const isExpanded = activeSlot === slot.slot;
  const isLocked = slot.isLocked;

  const homeId = homeTeam?.id;
  const awayId = awayTeam?.id;
  const { data: slotWeight } = useQuery<any>({
    queryKey: ["/api/pools", poolId, "pool-weight", slot.round, slot.slot, homeId, awayId],
    queryFn: () => {
      const params = new URLSearchParams();
      if (homeId) params.set("homeTeamId", homeId);
      if (awayId) params.set("awayTeamId", awayId);
      return fetch(`/api/pools/${poolId}/pool-weight/${slot.round}/${slot.slot}?${params}`, { credentials: "include" }).then((r) => r.json());
    },
    enabled: isExpanded && !!homeTeam && !!awayTeam,
    refetchInterval: 30000,
  });

  return (
    <div data-testid={`bracket-slot-${slot.slot}`} style={{ background:C.cardAlt, border:`1px solid ${C.border}`, borderRadius:10, overflow:"hidden" }}>
      <div style={{ display:"flex", alignItems:"center", gap:10, padding:"10px 14px", cursor: isLocked ? "default" : "pointer", opacity: isLocked ? 0.5 : 1 }}
        onClick={() => !isLocked && setActiveSlot(isExpanded ? null : slot.slot)}>
        <span style={{ fontSize:9, color:C.muted, width:52, flexShrink:0, fontWeight:700, letterSpacing:"0.05em" }}>{slot.slot}</span>
        {homeTeam && awayTeam ? (
          <div style={{ flex:1, display:"flex", alignItems:"center", gap:8, minWidth:0 }}>
            <span style={{ flex:1, fontSize:12, color: picked === homeId ? C.cream : C.text, fontWeight: picked === homeId ? 700 : 400, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap", display:"flex", alignItems:"center", gap:5 }}>
              <FlagImg cc={homeTeam.cc} size={16} />{homeTeam.name}
            </span>
            <span style={{ fontSize:9, color:C.muted, flexShrink:0 }}>vs</span>
            <span style={{ flex:1, fontSize:12, color: picked === awayId ? C.cream : C.text, fontWeight: picked === awayId ? 700 : 400, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap", textAlign:"right", display:"flex", alignItems:"center", justifyContent:"flex-end", gap:5 }}>
              {awayTeam.name}<FlagImg cc={awayTeam.cc} size={16} />
            </span>
          </div>
        ) : (
          <span style={{ flex:1, fontSize:11, color:C.muted, fontStyle:"italic" }}>{slot.homeSlotDesc ?? "TBD"} vs {slot.awaySlotDesc ?? "TBD"}</span>
        )}
        <div style={{ display:"flex", alignItems:"center", gap:6, flexShrink:0 }}>
          {picked && <span style={{ fontSize:9, background:C.greenDim, border:"1px solid rgba(74,222,128,0.3)", borderRadius:4, padding:"2px 6px", color:C.green, fontWeight:700 }}>✓</span>}
          {isLocked ? <Lock size={12} style={{ color:C.muted }} /> : isExpanded ? <ChevronUp size={13} style={{ color:C.muted }} /> : <ChevronDown size={13} style={{ color:C.muted }} />}
        </div>
      </div>

      {isExpanded && homeTeam && awayTeam && (
        <div style={{ borderTop:`1px solid ${C.border}`, padding:"12px 14px", display:"flex", flexDirection:"column", gap:10 }}>
          <div style={{ display:"flex", gap:8 }}>
            {[homeTeam, awayTeam].map((team) => {
              const isPicked = picked === team.id;
              const wt = slotWeight?.weights?.find((w: any) => w.teamId === team.id);
              return (
                <button key={team.id}
                  data-testid={`pick-bracket-${slot.slot}-${team.id}`}
                  onClick={() => setBracketPicks((prev) => ({ ...prev, [slot.slot]: team.id }))}
                  style={{ flex:1, padding:"12px 8px", borderRadius:10, cursor:"pointer", fontFamily:"inherit", transition:"all 0.15s",
                    background: isPicked ? C.greenDim : "rgba(255,255,255,0.03)",
                    border: `2px solid ${isPicked ? C.green : C.border}`,
                  }}>
                  <div style={{ display:"flex", alignItems:"center", justifyContent:"center", gap:6, marginBottom:5 }}>
                    <FlagImg cc={team.cc} size={20} />
                    <span style={{ fontSize:12, fontWeight:isPicked ? 800 : 600, color: isPicked ? C.cream : C.text }}>{team.name}</span>
                  </div>
                  <div style={{ textAlign:"center" }}>
                    <p style={{ fontSize:9, color:C.gold, fontWeight:700, margin:0 }}>{slotWeight?.flatPayout ?? BASE_POINTS[slot.round] ?? 2} pts if correct</p>
                    {wt && <p style={{ fontSize:8, color:C.muted, margin:"2px 0 0" }}>{wt.pickCount} picked ({wt.percentage}%)</p>}
                  </div>
                </button>
              );
            })}
          </div>
          {slotWeight && (
            <p style={{ fontSize:9, color:C.muted, textAlign:"center", margin:0 }}>
              {slotWeight.total} pool pick{slotWeight.total !== 1 ? "s" : ""} · flat {slotWeight.flatPayout ?? BASE_POINTS[slot.round] ?? 2} pts for correct pick
            </p>
          )}
        </div>
      )}

      {/* Bonus picks panel */}
      {homeTeam && awayTeam && (
        <BonusPicksPanel
          poolId={poolId}
          round={slot.round}
          matchSlot={slot.slot}
          homeTeam={homeTeam}
          awayTeam={awayTeam}
        />
      )}
    </div>
  );
}

const R32_KICKOFF = new Date("2026-06-29T18:00:00Z").getTime();

function useR32Countdown() {
  const calc = () => {
    const diff = R32_KICKOFF - Date.now();
    if (diff <= 0) return null;
    const days = Math.floor(diff / 86400000);
    const hours = Math.floor((diff % 86400000) / 3600000);
    const minutes = Math.floor((diff % 3600000) / 60000);
    const seconds = Math.floor((diff % 60000) / 1000);
    return { days, hours, minutes, seconds };
  };
  const [remaining, setRemaining] = useState(calc);
  useEffect(() => {
    const id = setInterval(() => setRemaining(calc()), 1000);
    return () => clearInterval(id);
  }, []);
  return remaining;
}

function BracketPicksWizard({ poolId }: { poolId: number }) {
  const [activeRound, setActiveRound] = useState("R32");
  const [bracketPicks, setBracketPicks] = useState<Record<string, string>>({});
  const [activeSlot, setActiveSlot] = useState<string | null>(null);
  const { toast } = useToast();
  const r32Countdown = useR32Countdown();

  const { data: savedBracketPicks = [] } = useQuery<any[]>({
    queryKey: ["/api/pools", poolId, "bracket-picks"],
    queryFn: () => fetch(`/api/pools/${poolId}/bracket-picks`, { credentials: "include" }).then((r) => r.json()),
  });

  const { data: bracketSlots } = useQuery<{ unlocked: boolean; slots: any[] }>({
    queryKey: ["/api/pools", poolId, "bracket-slots"],
    queryFn: () => fetch(`/api/pools/${poolId}/bracket-slots`, { credentials: "include" }).then((r) => r.json()),
    refetchInterval: 5 * 60 * 1000,
  });

  useEffect(() => {
    if (savedBracketPicks.length > 0) {
      const p: Record<string, string> = {};
      for (const pick of savedBracketPicks) p[pick.matchSlot] = pick.pickedTeamId;
      setBracketPicks(p);
    }
  }, [savedBracketPicks]);

  const saveMutation = useMutation({
    mutationFn: async () => {
      const picks = Object.entries(bracketPicks).map(([matchSlot, pickedTeamId]) => {
        const slotDef = WC_BRACKET_SLOTS.find((s) => s.slot === matchSlot);
        return { round: slotDef?.round ?? "R32", matchSlot, pickedTeamId };
      });
      const r = await apiRequest("POST", `/api/pools/${poolId}/bracket-picks`, { picks });
      if (!r.ok) throw new Error("Failed to save bracket picks");
      return r.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/pools", poolId, "bracket-picks"] });
      toast({ title: "Bracket picks saved!" });
    },
    onError: (e: any) => toast({ title: e.message, variant: "destructive" }),
  });

  const phase2Unlocked = bracketSlots?.unlocked ?? false;

  if (!phase2Unlocked) {
    return (
      <div style={{ display:"flex", flexDirection:"column", gap:12 }}>
        <div style={{ background:"rgba(212,160,23,0.06)", border:"1px solid rgba(212,160,23,0.2)", borderRadius:10, padding:"14px 16px", display:"flex", alignItems:"flex-start", gap:10 }}>
          <Lock size={15} style={{ color:C.gold, flexShrink:0, marginTop:1 }} />
          <div>
            <p style={{ fontSize:13, fontWeight:700, color:C.gold, marginBottom:4 }}>Phase 2 Locked Until Gap Day</p>
            <p style={{ fontSize:11, color:"rgba(212,160,23,0.7)", lineHeight:1.6, margin:0 }}>The knockout bracket unlocks on <strong>June 28, 2026</strong> once all group stage results are known. You'll pick winners across all 6 rounds.</p>
          </div>
        </div>
        <div style={{ display:"flex", flexWrap:"wrap", gap:8 }}>
          {KNOCKOUT_ROUNDS.map((r) => (
            <div key={r.id} style={{ background:C.cardAlt, border:`1px solid ${C.border}`, borderRadius:10, padding:"10px 14px", textAlign:"center", opacity:0.5 }}>
              <p style={{ fontSize:11, fontWeight:700, color:C.muted, margin:0 }}>{r.label}</p>
              <p style={{ fontSize:10, color:C.gold, marginTop:2 }}>+{r.basePoints} pts flat</p>
            </div>
          ))}
        </div>
        <div style={{ background:C.cardAlt, border:`1px solid ${C.border}`, borderRadius:10, padding:14, opacity:0.4, userSelect:"none" }}>
          <p style={{ fontSize:10, fontWeight:700, color:C.muted, marginBottom:8 }}>R32 Preview (16 matches)</p>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:6 }}>
            {WC_BRACKET_SLOTS.filter((s) => s.round === "R32").map((slot) => (
              <div key={slot.slot} style={{ background:"rgba(255,255,255,0.02)", borderRadius:6, padding:"6px 8px" }}>
                <p style={{ fontSize:9, fontWeight:700, color:C.muted, fontFamily:"monospace", margin:0 }}>{slot.slot}</p>
                <p style={{ fontSize:9, color:C.muted, margin:0, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{slot.homeSlotDesc} vs {slot.awaySlotDesc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  const allSlots = bracketSlots?.slots ?? WC_BRACKET_SLOTS;
  const roundGroups = KNOCKOUT_ROUNDS.map((r) => ({
    ...r,
    slots: allSlots.filter((s: any) => s.round === r.id),
  }));
  const pickedCount = Object.keys(bracketPicks).length;
  const totalPickable = allSlots.filter((s: any) => !s.isLocked).length;
  const resolvedR32Count = allSlots.filter((s: any) => s.round === "R32" && s.homeTeam && s.awayTeam).length;

  return (
    <div style={{ display:"flex", flexDirection:"column", gap:14 }}>

      {/* Gap Day unlock banner */}
      <div data-testid="phase2-unlock-banner" style={{ background:"linear-gradient(135deg,rgba(74,222,128,0.12),rgba(212,160,23,0.08))", border:"1px solid rgba(74,222,128,0.3)", borderRadius:10, padding:"12px 16px", display:"flex", alignItems:"flex-start", gap:10 }}>
        <span style={{ fontSize:18, flexShrink:0, lineHeight:1 }}>⚽</span>
        <div>
          <p style={{ fontSize:13, fontWeight:800, color:"rgba(74,222,128,1)", marginBottom:3 }}>Gap Day — Bracket is Open!</p>
          <p style={{ fontSize:11, color:"rgba(74,222,128,0.75)", lineHeight:1.6, margin:0 }}>
            All group stage results are in. <strong style={{ color:"rgba(74,222,128,0.95)" }}>{resolvedR32Count}/16</strong> Round of 32 matchups have been resolved with real qualified teams. Pick your winners before the first match kicks off on <strong style={{ color:"rgba(74,222,128,0.95)" }}>June 29</strong>.
          </p>
        </div>
      </div>

      {/* R32 countdown — hidden once first match locks */}
      {r32Countdown ? (
        <div data-testid="r32-countdown-banner" style={{ background:"linear-gradient(135deg,rgba(212,160,23,0.1),rgba(212,160,23,0.04))", border:"1px solid rgba(212,160,23,0.35)", borderRadius:10, padding:"12px 16px" }}>
          <p style={{ fontSize:10, fontWeight:700, color:"rgba(212,160,23,0.7)", letterSpacing:"0.08em", textTransform:"uppercase", margin:"0 0 8px 0" }}>⏱ Time left to submit picks</p>
          <div style={{ display:"flex", gap:12, alignItems:"baseline" }}>
            {[
              { value: r32Countdown.days,    label: "days" },
              { value: r32Countdown.hours,   label: "hrs" },
              { value: r32Countdown.minutes, label: "min" },
              { value: r32Countdown.seconds, label: "sec" },
            ].map(({ value, label }) => (
              <div key={label} style={{ textAlign:"center", minWidth:36, overflow:"hidden" }}>
                <span
                  key={value}
                  data-testid={`countdown-${label}`}
                  className="animate-digit-flip"
                  style={{ fontSize:22, fontWeight:800, color:C.gold, fontVariantNumeric:"tabular-nums", lineHeight:1 }}
                >
                  {String(value).padStart(2, "0")}
                </span>
                <span style={{ fontSize:9, color:"rgba(212,160,23,0.6)", fontWeight:600, letterSpacing:"0.06em", textTransform:"uppercase" }}>{label}</span>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div data-testid="r32-picks-locked-banner" style={{ background:"linear-gradient(135deg,rgba(239,68,68,0.1),rgba(239,68,68,0.04))", border:"1px solid rgba(239,68,68,0.35)", borderRadius:10, padding:"12px 16px", display:"flex", alignItems:"flex-start", gap:10 }}>
          <span style={{ fontSize:18, flexShrink:0, lineHeight:1 }}>🔒</span>
          <div>
            <p style={{ fontSize:13, fontWeight:800, color:"rgba(239,68,68,0.9)", marginBottom:3 }}>Picks locked — first match is live!</p>
            <p style={{ fontSize:11, color:"rgba(239,68,68,0.65)", lineHeight:1.6, margin:0 }}>
              R32 picks for matches that have already kicked off are frozen. You can still pick winners in future rounds as they unlock.
            </p>
          </div>
        </div>
      )}

      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between" }}>
        <div>
          <p style={{ fontSize:11, color:C.muted, margin:0 }}>Pick winners across all knockout rounds</p>
          <p style={{ fontSize:10, color:C.green, marginTop:3 }}>{pickedCount}/{totalPickable} unlocked picks made</p>
        </div>
        <button data-testid="button-save-bracket-picks" onClick={() => saveMutation.mutate()} disabled={saveMutation.isPending || pickedCount === 0}
          style={{ display:"flex", alignItems:"center", gap:6, padding:"9px 16px", borderRadius:8, background:`linear-gradient(135deg,${C.gold},#c8860d)`, border:"none", color:"#080e0a", fontSize:11, fontWeight:800, cursor:"pointer", fontFamily:"inherit", opacity: pickedCount === 0 ? 0.5 : 1 }}>
          {saveMutation.isPending ? <Loader2 size={12} className="animate-spin" /> : null}
          Save Picks
        </button>
      </div>

      <div style={{ background:C.goldDim, border:"1px solid rgba(212,160,23,0.2)", borderRadius:8, padding:"10px 14px", fontSize:11, color:C.gold, lineHeight:1.5 }}>
        ⚽ <strong>Flat Points:</strong> Every correct pick scores a fixed number of points (2→4→8→16→32). Bonus picks available on each match card for extra points — clean sheets, exact scores, and penalties!
      </div>

      <div style={{ display:"flex", flexWrap:"wrap", gap:6 }}>
        {KNOCKOUT_ROUNDS.map((r) => {
          const roundSlots = allSlots.filter((s: any) => s.round === r.id);
          const roundPicked = roundSlots.filter((s: any) => bracketPicks[s.slot]).length;
          const isRndLocked = roundSlots.every((s: any) => s.isLocked);
          const isActive = activeRound === r.id;
          const isDone = roundPicked === roundSlots.length && roundSlots.length > 0;
          return (
            <button key={r.id} onClick={() => setActiveRound(r.id)}
              style={{ padding:"7px 14px", borderRadius:8, fontSize:11, fontWeight:700, cursor:"pointer", fontFamily:"inherit", transition:"all 0.15s",
                background: isActive ? `linear-gradient(135deg,${C.gold},#c8860d)` : isDone ? C.greenDim : isRndLocked ? "rgba(255,255,255,0.02)" : "rgba(255,255,255,0.04)",
                border: isActive ? "none" : isDone ? `1px solid rgba(74,222,128,0.3)` : `1px solid ${C.border}`,
                color: isActive ? "#080e0a" : isDone ? C.green : isRndLocked ? C.muted : C.text,
              }}>
              {r.label} <span style={{ opacity:0.6 }}>{roundPicked}/{roundSlots.length}</span>
            </button>
          );
        })}
      </div>

      {roundGroups.map((rg) => {
        if (rg.id !== activeRound) return null;
        return (
          <div key={rg.id} style={{ display:"flex", flexDirection:"column", gap:8 }}>
            <div style={{ display:"flex", alignItems:"center", gap:8 }}>
              <span style={{ fontSize:13, fontWeight:800, color:C.cream }}>{rg.label}</span>
              <span style={{ fontSize:9, background:C.goldDim, border:"1px solid rgba(212,160,23,0.25)", borderRadius:6, padding:"2px 8px", color:C.gold, fontWeight:700 }}>{rg.basePoints} pts flat</span>
            </div>
            {rg.slots.map((slot: any) => (
              <BracketSlotRow key={slot.slot} slot={slot} poolId={poolId} bracketPicks={bracketPicks} setBracketPicks={setBracketPicks} activeSlot={activeSlot} setActiveSlot={setActiveSlot} />
            ))}
          </div>
        );
      })}
    </div>
  );
}

function isLiveMatchTime(): boolean {
  const now = new Date();
  const utcHour = now.getUTCHours();
  const utcDate = now.toISOString().slice(0, 10);
  const tournamentStart = "2026-06-11";
  const tournamentEnd = "2026-07-19";
  if (utcDate < tournamentStart || utcDate > tournamentEnd) return false;
  return utcHour >= 12 && utcHour < 23;
}

function useRelativeTime(isoString: string | null | undefined): string {
  const [label, setLabel] = useState<string>("");
  useEffect(() => {
    if (!isoString) { setLabel(""); return; }
    function update() {
      const diff = Math.floor((Date.now() - new Date(isoString!).getTime()) / 1000);
      if (diff < 60) setLabel("just now");
      else if (diff < 3600) setLabel(`${Math.floor(diff / 60)}m ago`);
      else setLabel(`${Math.floor(diff / 3600)}h ago`);
    }
    update();
    const id = setInterval(update, 30_000);
    return () => clearInterval(id);
  }, [isoString]);
  return label;
}

// ── Winners Announcement Screen ────────────────────────────────────────────

function WinnersScreen({ winners, prizeDescription }: {
  winners: { champion: any; chaosAgent: any; groupStageKing: any } | null;
  prizeDescription: string | null;
}) {
  if (!winners) return null;

  function playerName(s: any) {
    if (!s) return "TBD";
    return s.firstName ? `${s.firstName} ${s.lastName || ""}`.trim() : "Player";
  }

  const hasPrize = !!prizeDescription?.trim();
  const prizeLine = hasPrize ? prizeDescription : null;

  return (
    <div data-testid="winners-screen" style={{ background:`radial-gradient(ellipse at 50% 0%,rgba(212,160,23,0.18),transparent 70%), linear-gradient(180deg,#0e1a0f,#080e0a)`, border:`1px solid rgba(212,160,23,0.35)`, borderTop:`3px solid ${C.gold}`, borderRadius:16, padding:24, marginBottom:20 }}>
      <div style={{ textAlign:"center", marginBottom:20 }}>
        <div style={{ fontSize:32, marginBottom:8 }}>🏆</div>
        <h2 style={{ fontSize:20, fontWeight:900, color:C.cream, margin:0, letterSpacing:"-0.02em" }}>Final Results — WC 2026</h2>
        <p style={{ fontSize:12, color:C.gold, marginTop:4 }}>Tournament complete. Congratulations to the winners!</p>
        {prizeLine && (
          <div style={{ background:"rgba(212,160,23,0.1)", border:"1px solid rgba(212,160,23,0.25)", borderRadius:8, padding:"8px 14px", marginTop:12, display:"inline-block" }}>
            <p style={{ fontSize:12, color:C.gold, fontWeight:700, margin:0 }}>💰 {prizeLine}</p>
          </div>
        )}
      </div>

      <div style={{ display:"flex", flexDirection:"column", gap:12 }}>
        {/* Overall Champion */}
        <div data-testid="winner-champion" style={{ background:"rgba(212,160,23,0.10)", border:"2px solid rgba(212,160,23,0.4)", borderRadius:14, padding:16, display:"flex", alignItems:"center", gap:14 }}>
          <div style={{ width:44, height:44, borderRadius:"50%", background:`linear-gradient(135deg,${C.gold},#c8860d)`, display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
            <Crown size={22} style={{ color:"#080e0a" }} />
          </div>
          <div style={{ flex:1 }}>
            <p style={{ fontSize:9, fontWeight:700, color:C.gold, textTransform:"uppercase", letterSpacing:"0.2em", margin:0 }}>Overall Champion</p>
            <p style={{ fontSize:18, fontWeight:900, color:C.cream, margin:"4px 0 0", letterSpacing:"-0.01em" }}>{playerName(winners.champion)}</p>
            <p style={{ fontSize:11, color:C.gold, margin:"2px 0 0" }}>{Math.round(winners.champion?.totalPoints ?? 0)} pts · Rank #1</p>
          </div>
          {hasPrize && <div style={{ textAlign:"right", flexShrink:0 }}><p style={{ fontSize:11, color:C.gold, fontWeight:700, margin:0 }}>80%</p><p style={{ fontSize:9, color:C.muted }}>of pot</p></div>}
        </div>

        {/* Chaos Agent */}
        {winners.chaosAgent && (
          <div data-testid="winner-chaos-agent" style={{ background:"rgba(212,160,23,0.06)", border:"1px solid rgba(212,160,23,0.25)", borderRadius:14, padding:16, display:"flex", alignItems:"center", gap:14 }}>
            <div style={{ width:44, height:44, borderRadius:"50%", background:"rgba(212,160,23,0.15)", border:`1px solid rgba(212,160,23,0.4)`, display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
              <Zap size={22} style={{ color:C.gold }} />
            </div>
            <div style={{ flex:1 }}>
              <p style={{ fontSize:9, fontWeight:700, color:C.gold, textTransform:"uppercase", letterSpacing:"0.2em", margin:0 }}>⚡ Chaos Agent</p>
              <p style={{ fontSize:16, fontWeight:800, color:C.cream, margin:"4px 0 0" }}>{playerName(winners.chaosAgent)}</p>
              <p style={{ fontSize:11, color:C.muted, margin:"2px 0 0" }}>Biggest solo upset pick in the pool</p>
            </div>
            {hasPrize && <div style={{ textAlign:"right", flexShrink:0 }}><p style={{ fontSize:11, color:C.gold, fontWeight:700, margin:0 }}>10%</p><p style={{ fontSize:9, color:C.muted }}>of pot</p></div>}
          </div>
        )}

        {/* Group Stage King */}
        {winners.groupStageKing && (
          <div data-testid="winner-group-stage-king" style={{ background:"rgba(74,222,128,0.05)", border:"1px solid rgba(74,222,128,0.2)", borderRadius:14, padding:16, display:"flex", alignItems:"center", gap:14 }}>
            <div style={{ width:44, height:44, borderRadius:"50%", background:C.greenDim, border:`1px solid rgba(74,222,128,0.3)`, display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
              <Star size={22} style={{ color:C.green }} />
            </div>
            <div style={{ flex:1 }}>
              <p style={{ fontSize:9, fontWeight:700, color:C.green, textTransform:"uppercase", letterSpacing:"0.2em", margin:0 }}>Group Stage King</p>
              <p style={{ fontSize:16, fontWeight:800, color:C.cream, margin:"4px 0 0" }}>{playerName(winners.groupStageKing)}</p>
              <p style={{ fontSize:11, color:C.muted, margin:"2px 0 0" }}>Highest group stage score: {Math.round(winners.groupStageKing?.groupPoints ?? 0)} pts</p>
            </div>
            {hasPrize && <div style={{ textAlign:"right", flexShrink:0 }}><p style={{ fontSize:11, color:C.green, fontWeight:700, margin:0 }}>10%</p><p style={{ fontSize:9, color:C.muted }}>of pot</p></div>}
          </div>
        )}
      </div>

      {hasPrize && (
        <p style={{ fontSize:10, color:C.muted, textAlign:"center", marginTop:16, marginBottom:0, opacity:0.7 }}>Drpr.ai does not collect, hold, or distribute any funds.</p>
      )}
    </div>
  );
}

function LeaderboardTab({ poolId }: { poolId: number }) {
  const [expanded, setExpanded] = useState<string | null>(null);
  const live = isLiveMatchTime();
  const [lbPage, setLbPage] = useState(1);
  const [accumulatedScores, setAccumulatedScores] = useState<any[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [hasMore, setHasMore] = useState(false);

  const { data: leaderboard, isLoading } = useQuery<{
    scores: any[];
    lastUpdated: string | null;
    prizeDescription: string | null;
    isComplete: boolean;
    winners: { champion: any; chaosAgent: any; groupStageKing: any } | null;
    totalCount: number;
    hasMore: boolean;
    page: number;
  }>({
    queryKey: ["/api/pools", poolId, "leaderboard", lbPage],
    queryFn: () => fetch(`/api/pools/${poolId}/leaderboard?page=${lbPage}`, { credentials: "include" }).then((r) => r.json()),
    refetchInterval: live ? 2 * 60 * 1000 : 5 * 60 * 1000,
  });

  // Accumulate scores across pages.
  // useEffect (not render-time mutation) ensures:
  //   - Page-1 refetches (live polling) always replace the displayed data with fresh scores
  //   - Page-2+ data only appends when the user explicitly clicks "Load more"
  const prevPageRef = useRef(0);
  const prevDataRef = useRef<typeof leaderboard | undefined>(undefined);
  useEffect(() => {
    if (!leaderboard) return;
    if (leaderboard === prevDataRef.current) return; // same reference, no update needed
    prevDataRef.current = leaderboard;
    const pageChanged = leaderboard.page !== prevPageRef.current;
    if (leaderboard.page === 1) {
      // Always refresh: handles both initial load and live-polling refetches
      setAccumulatedScores(leaderboard.scores ?? []);
    } else if (pageChanged) {
      // Only append when the user actually navigated to a new page
      setAccumulatedScores((prev) => [...prev, ...(leaderboard.scores ?? [])]);
    }
    prevPageRef.current = leaderboard.page;
    setTotalCount(leaderboard.totalCount ?? 0);
    setHasMore(leaderboard.hasMore ?? false);
  }, [leaderboard]);

  const scores: any[] = accumulatedScores.length > 0 ? accumulatedScores : (leaderboard?.scores ?? []);
  const lastUpdatedLabel = useRelativeTime(leaderboard?.lastUpdated);

  const { data: card } = useQuery<any>({
    queryKey: ["/api/pools", poolId, "my-card"],
    queryFn: () => fetch(`/api/pools/${poolId}/my-card`, { credentials: "include" }).then((r) => r.json()),
  });

  const { toast } = useToast();
  const cardRef = useRef<HTMLDivElement>(null);

  async function downloadCard() {
    try {
      const { default: html2canvas } = await import("html2canvas");
      const canvas = await html2canvas(cardRef.current!);
      const link = document.createElement("a");
      link.download = "chaos-pool-card.png";
      link.href = canvas.toDataURL();
      link.click();
      toast({ title: "Card downloaded!" });
    } catch {
      toast({ title: "Download failed", variant: "destructive" });
    }
  }

  if (isLoading) return (
    <div style={{ display:"flex", justifyContent:"center", padding:"48px 0" }}>
      <Loader2 size={24} className="animate-spin" style={{ color:C.gold }} />
    </div>
  );

  const isComplete = leaderboard?.isComplete ?? false;
  const winners = leaderboard?.winners ?? null;
  const prizeDescription = leaderboard?.prizeDescription ?? null;

  return (
    <div style={{ display:"flex", flexDirection:"column", gap:16 }}>
      {/* Winners announcement (post-tournament) */}
      {isComplete && winners && (
        <WinnersScreen winners={winners} prizeDescription={prizeDescription} />
      )}

      {/* Prize description (pre-tournament or no winner screen) */}
      {!isComplete && prizeDescription && (
        <div data-testid="prize-description-display" style={{ background:"rgba(212,160,23,0.08)", border:"1px solid rgba(212,160,23,0.25)", borderRadius:10, padding:"10px 14px" }}>
          <p style={{ fontSize:11, color:C.gold, fontWeight:700, margin:0 }}>💰 Prize Pot: {prizeDescription}</p>
          <p style={{ fontSize:9, color:C.muted, marginTop:4 }}>Drpr.ai does not collect, hold, or distribute any funds.</p>
        </div>
      )}

      {/* Performance Card */}
      {card && (
        <div ref={cardRef} style={{ background:`radial-gradient(ellipse at 60% 30%,rgba(212,160,23,0.12),transparent 70%), linear-gradient(160deg,#0e1a0f 0%,#080e0a 100%)`, border:`1px solid ${C.border}`, borderTop:`2px solid ${C.gold}`, borderRadius:16, padding:20, position:"relative", overflow:"hidden" }}>
          <div style={{ position:"absolute", inset:0, opacity:0.04, backgroundImage:"repeating-linear-gradient(0deg,transparent,transparent 38px,#4ade80 38px,#4ade80 39px),repeating-linear-gradient(90deg,transparent,transparent 38px,#4ade80 38px,#4ade80 39px)", pointerEvents:"none" }} />

          <div style={{ position:"relative", zIndex:1 }}>
            <div style={{ display:"flex", alignItems:"flex-start", justifyContent:"space-between", marginBottom:16 }}>
              <div>
                <p style={{ fontSize:8, fontWeight:700, color:C.gold, textTransform:"uppercase", letterSpacing:"0.2em", margin:0 }}>Chaos Pools · WC 2026</p>
                <h3 style={{ fontSize:16, fontWeight:900, color:C.cream, margin:"4px 0 0", letterSpacing:"-0.01em" }}>{card.poolName}</h3>
              </div>
              {card.chaosAgent && (
                <div style={{ background:"rgba(212,160,23,0.15)", border:"1px solid rgba(212,160,23,0.35)", borderRadius:8, padding:"6px 10px" }}>
                  <p style={{ fontSize:10, fontWeight:800, color:C.gold, margin:0 }}>⚡ CHAOS AGENT</p>
                </div>
              )}
            </div>

            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:8, marginBottom:14 }}>
              <div style={{ background:"rgba(255,255,255,0.04)", borderRadius:10, padding:"12px 8px", textAlign:"center" }}>
                <p style={{ fontSize:22, fontWeight:900, color:C.gold, margin:0 }}>#{card.rank ?? "—"}</p>
                <p style={{ fontSize:9, color:C.muted, marginTop:2 }}>of {card.totalMembers}</p>
              </div>
              <div style={{ background:"rgba(255,255,255,0.04)", borderRadius:10, padding:"12px 8px", textAlign:"center" }}>
                <p style={{ fontSize:22, fontWeight:900, color:C.green, margin:0 }}>{Math.round(card.totalPoints ?? 0)}</p>
                <p style={{ fontSize:9, color:C.muted, marginTop:2 }}>total pts</p>
              </div>
              <div style={{ background:"rgba(255,255,255,0.04)", borderRadius:10, padding:"12px 8px", textAlign:"center" }}>
                <p style={{ fontSize:22, fontWeight:900, color:(card.weeklyDelta ?? 0) >= 0 ? C.green : C.red, margin:0 }}>
                  {(card.weeklyDelta ?? 0) >= 0 ? "+" : ""}{Math.round(card.weeklyDelta ?? 0)}
                </p>
                <p style={{ fontSize:9, color:C.muted, marginTop:2 }}>this week</p>
              </div>
            </div>

            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:6, marginBottom:12, textAlign:"center" }}>
              <div><p style={{ fontSize:9, color:C.muted, margin:0 }}>Group</p><p style={{ fontSize:13, fontWeight:800, color:C.cream, marginTop:2 }}>{Math.round(card.groupPoints ?? 0)}</p></div>
              <div><p style={{ fontSize:9, color:C.muted, margin:0 }}>Bracket</p><p style={{ fontSize:13, fontWeight:800, color:C.cream, marginTop:2 }}>{Math.round(card.bracketPoints ?? 0)}</p></div>
              <div><p style={{ fontSize:9, color:C.muted, margin:0 }}>Wildcard</p><p style={{ fontSize:13, fontWeight:800, color:C.cream, marginTop:2 }}>{Math.round(card.wildcardPoints ?? 0)}</p></div>
            </div>

            {card.bestPickDesc && (
              <p style={{ fontSize:11, color:C.green, background:C.greenDim, borderRadius:8, padding:"8px 12px", margin:"0 0 12px" }}>🎯 Best pick: {card.bestPickDesc}</p>
            )}

            <p style={{ fontSize:9, color:C.muted, textAlign:"right", margin:0, opacity:0.6 }}>Powered by Drpr.ai</p>
          </div>
        </div>
      )}

      {card && (
        <div style={{ display:"flex", gap:8, flexWrap:"wrap" }}>
          <button onClick={downloadCard} style={{ display:"flex", alignItems:"center", gap:5, padding:"7px 12px", borderRadius:8, background:"rgba(255,255,255,0.05)", border:`1px solid ${C.border}`, color:C.muted, fontSize:11, fontWeight:700, cursor:"pointer", fontFamily:"inherit" }}>
            <Download size={12} /> PNG
          </button>
          <button data-testid="button-share-x"
            onClick={() => {
              const text = `I'm ranked #${card.rank} in ${card.poolName} with ${Math.round(card.totalPoints)} pts!${card.chaosAgent ? " ⚡ Chaos Agent!" : ""} Join: ${window.location.origin}/pools/join/${card.inviteCode} #ChaosPoolsWC2026`;
              window.open(`https://x.com/intent/tweet?text=${encodeURIComponent(text)}`, "_blank");
            }}
            style={{ display:"flex", alignItems:"center", gap:5, padding:"7px 12px", borderRadius:8, background:"rgba(255,255,255,0.05)", border:`1px solid ${C.border}`, color:C.muted, fontSize:11, fontWeight:700, cursor:"pointer", fontFamily:"inherit" }}>
            <Share2 size={12} /> X
          </button>
          <button data-testid="button-share-linkedin"
            onClick={() => {
              const url = `${window.location.origin}/pools/join/${card.inviteCode}`;
              const summary = `I'm competing in Chaos Pools — a World Cup 2026 prediction game. Join my pool! ${card.chaosAgent ? "⚡ I earned the Chaos Agent badge!" : ""}`;
              window.open(`https://www.linkedin.com/shareArticle?mini=true&url=${encodeURIComponent(url)}&title=${encodeURIComponent("Chaos Pools — WC 2026")}&summary=${encodeURIComponent(summary)}`, "_blank");
            }}
            style={{ display:"flex", alignItems:"center", gap:5, padding:"7px 12px", borderRadius:8, background:"rgba(255,255,255,0.05)", border:`1px solid ${C.border}`, color:C.muted, fontSize:11, fontWeight:700, cursor:"pointer", fontFamily:"inherit" }}>
            <Share2 size={12} /> LinkedIn
          </button>
          <button data-testid="button-share-facebook"
            onClick={() => {
              const url = `${window.location.origin}/pools/join/${card.inviteCode}`;
              window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`, "_blank");
            }}
            style={{ display:"flex", alignItems:"center", gap:5, padding:"7px 12px", borderRadius:8, background:"rgba(255,255,255,0.05)", border:`1px solid ${C.border}`, color:C.muted, fontSize:11, fontWeight:700, cursor:"pointer", fontFamily:"inherit" }}>
            <Share2 size={12} /> FB
          </button>
        </div>
      )}

      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between" }}>
        <div style={{ display:"flex", alignItems:"center", gap:8 }}>
          <h3 style={{ fontSize:14, fontWeight:800, color:C.cream, margin:0 }}>Standings</h3>
          {live && (
            <span data-testid="badge-live" style={{ display:"inline-flex", alignItems:"center", gap:4, background:"rgba(74,222,128,0.12)", border:"1px solid rgba(74,222,128,0.3)", borderRadius:6, padding:"2px 7px", fontSize:9, fontWeight:800, color:"#4ade80", textTransform:"uppercase", letterSpacing:"0.1em" }}>
              <span style={{ width:5, height:5, borderRadius:"50%", background:"#4ade80", display:"inline-block", animation:"pulse 1.5s ease-in-out infinite" }} />
              Live
            </span>
          )}
        </div>
        {lastUpdatedLabel && (
          <span data-testid="text-last-updated" style={{ fontSize:10, color:C.muted }}>Updated {lastUpdatedLabel}</span>
        )}
      </div>

      <div style={{ display:"flex", flexDirection:"column", gap:6 }}>
        {scores.length === 0 ? (
          <div style={{ textAlign:"center", padding:"32px 0", fontSize:12, color:C.muted }}>No scores yet — picks are being scored as matches finish.</div>
        ) : scores.map((score: any) => {
          const rankEmoji = score.rank === 1 ? "🥇" : score.rank === 2 ? "🥈" : score.rank === 3 ? "🥉" : null;
          const isExpanded_ = expanded === score.userId;
          return (
            <div key={score.userId}>
              <div data-testid={`leaderboard-row-${score.rank}`}
                onClick={() => setExpanded(isExpanded_ ? null : score.userId)}
                style={{ display:"flex", alignItems:"center", gap:10, padding:"11px 14px", borderRadius: isExpanded_ ? "10px 10px 0 0" : 10, cursor:"pointer", transition:"background 0.15s",
                  background: score.isCurrentUser ? "rgba(212,160,23,0.08)" : "rgba(255,255,255,0.03)",
                  border: `1px solid ${score.isCurrentUser ? "rgba(212,160,23,0.25)" : C.border}`,
                  borderBottom: isExpanded_ ? `1px solid ${C.border}` : undefined,
                }}>
                <span style={{ fontSize:13, width:22, textAlign:"center", fontWeight:800, color: score.rank <= 3 ? C.gold : C.muted }}>
                  {rankEmoji || score.rank}
                </span>
                <span style={{ flex:1, fontSize:13, fontWeight:600, color: score.isCurrentUser ? C.cream : C.text, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
                  {score.firstName ? `${score.firstName} ${score.lastName || ""}`.trim() : "Player"}
                  {score.chaosAgent && <span style={{ marginLeft:6, fontSize:10, color:C.gold }}>⚡</span>}
                  {score.isCurrentUser && <span style={{ marginLeft:6, fontSize:9, color:C.gold, fontWeight:700 }}>YOU</span>}
                </span>
                <div style={{ textAlign:"right" }}>
                  <p style={{ fontSize:13, fontWeight:800, color: score.isCurrentUser ? C.gold : C.cream, margin:0 }}>{Math.round(score.totalPoints ?? 0)} pts</p>
                  {score.weeklyDelta !== 0 && (
                    <p style={{ fontSize:10, color:(score.weeklyDelta ?? 0) >= 0 ? C.green : C.red, margin:0 }}>
                      {(score.weeklyDelta ?? 0) >= 0 ? "▲" : "▼"} {Math.abs(Math.round(score.weeklyDelta ?? 0))}
                    </p>
                  )}
                </div>
                {isExpanded_ ? <ChevronUp size={13} style={{ color:C.muted, flexShrink:0 }} /> : <ChevronDown size={13} style={{ color:C.muted, flexShrink:0 }} />}
              </div>
              {isExpanded_ && (
                <div style={{ background:"rgba(255,255,255,0.015)", border:`1px solid ${C.border}`, borderTop:"none", borderRadius:"0 0 10px 10px", padding:"12px 16px" }}>
                  <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:6, textAlign:"center" }}>
                    <div><p style={{ fontSize:9, color:C.muted, margin:0 }}>Group Picks</p><p style={{ fontSize:13, fontWeight:800, color:C.cream, marginTop:2 }}>{Math.round(score.groupPoints ?? 0)} pts</p></div>
                    <div><p style={{ fontSize:9, color:C.muted, margin:0 }}>Bracket</p><p style={{ fontSize:13, fontWeight:800, color:C.cream, marginTop:2 }}>{Math.round(score.bracketPoints ?? 0)} pts</p></div>
                    <div><p style={{ fontSize:9, color:C.muted, margin:0 }}>Wildcard</p><p style={{ fontSize:13, fontWeight:800, color:C.cream, marginTop:2 }}>{Math.round(score.wildcardPoints ?? 0)} pts</p></div>
                  </div>
                  {score.bestPickDesc && <p style={{ fontSize:11, color:C.green, marginTop:8, marginBottom:0 }}>🎯 {score.bestPickDesc}</p>}
                </div>
              )}
            </div>
          );
        })}
        {hasMore && (
          <button
            data-testid="leaderboard-load-more"
            onClick={() => setLbPage((p) => p + 1)}
            style={{ width:"100%", marginTop:8, padding:"10px 0", borderRadius:8, background:"rgba(255,255,255,0.04)", border:`1px solid ${C.border}`, color:C.muted, fontSize:12, fontWeight:600, cursor:"pointer", fontFamily:"inherit" }}
          >
            Load more ({scores.length} of {totalCount} shown)
          </button>
        )}
      </div>
    </div>
  );
}

function CommissionerSettings({ pool }: { pool: any }) {
  const [name, setName] = useState(pool.name);
  const [description, setDescription] = useState(pool.description || "");
  const [commissionerNote, setCommissionerNote] = useState(pool.commissionerNote || "");
  const [buyInAmount, setBuyInAmount] = useState(parseFloat(pool.buyInAmount || "0") > 0 ? String(parseFloat(pool.buyInAmount)) : "");
  const [prizeDescription, setPrizeDescription] = useState(pool.prizeDescription || "");
  const { toast } = useToast();

  const parsedBuyIn = parseFloat(buyInAmount) || 0;

  const saveMutation = useMutation({
    mutationFn: async () => {
      const r = await apiRequest("PATCH", `/api/pools/${pool.id}`, {
        name, description, commissionerNote, buyInAmount: parsedBuyIn,
        prizeDescription: prizeDescription.trim() || null,
      });
      if (!r.ok) throw new Error("Failed to save");
      return r.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/pools", pool.id] });
      toast({ title: "Pool settings saved!" });
    },
    onError: (e: any) => toast({ title: e.message, variant: "destructive" }),
  });

  function copyLink() {
    navigator.clipboard.writeText(`${window.location.origin}/pools/join/${pool.inviteCode}`);
    toast({ title: "Invite link copied!" });
  }

  return (
    <div style={{ display:"flex", flexDirection:"column", gap:14 }}>
      <div style={{ background:C.card, border:`1px solid ${C.border}`, borderTop:`2px solid ${C.gold}`, borderRadius:12, padding:18, display:"flex", flexDirection:"column", gap:14 }}>
        <h3 style={{ fontSize:14, fontWeight:800, color:C.cream, margin:0 }}>Pool Settings</h3>
        <div>
          <label style={{ fontSize:9, color:C.muted, textTransform:"uppercase", letterSpacing:"0.12em", fontWeight:700, display:"block", marginBottom:6 }}>Pool Name</label>
          <input value={name} onChange={(e) => setName(e.target.value)} style={inputStyle} />
        </div>
        <div>
          <label style={{ fontSize:9, color:C.muted, textTransform:"uppercase", letterSpacing:"0.12em", fontWeight:700, display:"block", marginBottom:6 }}>Description</label>
          <input value={description} onChange={(e) => setDescription(e.target.value)} style={inputStyle} />
        </div>
        <div>
          <label style={{ fontSize:9, color:C.muted, textTransform:"uppercase", letterSpacing:"0.12em", fontWeight:700, display:"block", marginBottom:6 }}>Buy-In Amount <span style={{ fontWeight:400, textTransform:"none", letterSpacing:0 }}>(optional)</span></label>
          <div style={{ position:"relative" }}>
            <span style={{ position:"absolute", left:12, top:"50%", transform:"translateY(-50%)", color:C.muted, fontSize:14, fontWeight:700, pointerEvents:"none" }}>$</span>
            <input data-testid="input-settings-buy-in" type="number" min="0" step="5" value={buyInAmount} onChange={(e) => setBuyInAmount(e.target.value)} placeholder="0" style={{ ...inputStyle, paddingLeft:26 }} />
          </div>
          {parsedBuyIn > 0 && (
            <p style={{ fontSize:10, color:C.muted, marginTop:4 }}>Prize pot: <strong style={{ color:"#d4a017" }}>${(parsedBuyIn * (pool.memberCount || 1)).toFixed(0)}</strong> with current {pool.memberCount || 1} {(pool.memberCount || 1) === 1 ? "member" : "members"}</p>
          )}
        </div>
        <div>
          <label style={{ fontSize:9, color:C.muted, textTransform:"uppercase", letterSpacing:"0.12em", fontWeight:700, display:"block", marginBottom:6 }}>Prize Pot & Split <span style={{ fontWeight:400, textTransform:"none", letterSpacing:0 }}>(display only)</span></label>
          <input
            data-testid="input-settings-prize-description"
            value={prizeDescription}
            onChange={(e) => setPrizeDescription(e.target.value)}
            placeholder="e.g. $200 total — 1st: 80%, Chaos Agent: 10%, Group King: 10%"
            style={inputStyle}
          />
          <p style={{ fontSize:9, color:C.muted, marginTop:4 }}>Shown on the leaderboard. Drpr holds no money.</p>
        </div>
        <div>
          <label style={{ fontSize:9, color:C.muted, textTransform:"uppercase", letterSpacing:"0.12em", fontWeight:700, display:"block", marginBottom:6 }}>Commissioner Note</label>
          <textarea value={commissionerNote} onChange={(e) => setCommissionerNote(e.target.value)} rows={3}
            placeholder="Buy-in instructions, e.g. 'Send $20 via e-transfer to...'"
            style={{ ...inputStyle, resize:"none" }} />
          <p style={{ fontSize:9, color:C.muted, marginTop:4 }}>Drpr.ai does not collect, hold, or distribute any funds.</p>
        </div>
        <button data-testid="button-save-pool-settings" onClick={() => saveMutation.mutate()} disabled={saveMutation.isPending}
          style={{ padding:"11px 20px", borderRadius:9, background:`linear-gradient(135deg,${C.gold},#c8860d)`, border:"none", color:"#080e0a", fontWeight:800, fontSize:13, cursor:"pointer", display:"flex", alignItems:"center", gap:8, fontFamily:"inherit" }}>
          {saveMutation.isPending ? <Loader2 size={14} className="animate-spin" /> : null} Save Settings
        </button>
      </div>
      <div style={{ background:C.card, border:`1px solid ${C.border}`, borderRadius:12, padding:18 }}>
        <h3 style={{ fontSize:14, fontWeight:800, color:C.cream, marginBottom:12 }}>Invite Link</h3>
        <div style={{ display:"flex", alignItems:"center", gap:8, background:"rgba(255,255,255,0.04)", borderRadius:8, padding:"10px 14px" }}>
          <span style={{ flex:1, fontSize:12, fontFamily:"monospace", color:C.cream, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
            {window.location.origin}/pools/join/{pool.inviteCode}
          </span>
          <button data-testid="button-copy-invite-link" onClick={copyLink}
            style={{ display:"flex", alignItems:"center", gap:5, padding:"6px 12px", borderRadius:7, background:C.goldDim, border:"1px solid rgba(212,160,23,0.3)", color:C.gold, fontSize:11, fontWeight:700, cursor:"pointer", flexShrink:0, fontFamily:"inherit" }}>
            <Copy size={11} /> Copy
          </button>
        </div>
        <p style={{ fontSize:10, color:C.muted, marginTop:8 }}>Invite code: <strong style={{ color:C.gold, fontFamily:"monospace" }}>{pool.inviteCode}</strong></p>
      </div>
    </div>
  );
}

// ── How to Play Tab ──────────────────────────────────────────────────────────

function HowToPlayTab() {
  const card: React.CSSProperties = {
    background: C.cardAlt, border: `1px solid ${C.border}`, borderRadius: 12, padding: "18px 20px", marginBottom: 14,
  };
  const heading: React.CSSProperties = {
    fontSize: 11, fontWeight: 800, color: C.muted, textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 12,
  };
  const row: React.CSSProperties = {
    display: "flex", justifyContent: "space-between", alignItems: "center",
    padding: "9px 0", borderBottom: `1px solid rgba(255,255,255,0.04)`,
  };
  const lastRow: React.CSSProperties = { display: "flex", justifyContent: "space-between", alignItems: "center", padding: "9px 0" };
  const label: React.CSSProperties = { fontSize: 13, color: C.text };
  const pts: React.CSSProperties = { fontSize: 13, fontWeight: 800, color: C.gold, minWidth: 60, textAlign: "right" };
  const muted: React.CSSProperties = { fontSize: 11, color: C.muted };

  const dates = [
    { date: "Jun 11 · 1:00 PM ET", event: "Group picks lock — no changes after this", hot: true },
    { date: "Jun 12", event: "Tournament opens — 48 group stage matches begin" },
    { date: "Jun 28", event: "Round of 32 begins — bracket picks open" },
    { date: "Jul 4", event: "Round of 16" },
    { date: "Jul 9", event: "Quarterfinals" },
    { date: "Jul 14", event: "Semifinals" },
    { date: "Jul 19", event: "🏆 Final — champion crowned" },
  ];

  return (
    <div style={{ paddingBottom: 20 }}>
      {/* Intro */}
      <div style={{ ...card, borderTop: `2px solid ${C.gold}`, marginBottom: 20 }}>
        <h2 style={{ fontSize: 18, fontWeight: 900, color: C.cream, margin: "0 0 6px" }}>How Chaos Pools Works</h2>
        <p style={{ fontSize: 13, color: C.muted, lineHeight: 1.7, margin: 0 }}>
          Predict every match — group stage results, knockout winners, and bonus picks.
          Flat points only: no popularity multiplier, no handicaps. Biggest total wins.
        </p>
      </div>

      {/* Key Dates */}
      <div style={card}>
        <p style={heading}>📅 Key Dates</p>
        {dates.map((d, i) => (
          <div key={i} style={i < dates.length - 1 ? row : lastRow}>
            <div style={{ display: "flex", flexDirection: "column", gap: 1 }}>
              <span style={{ ...label, color: d.hot ? C.gold : C.text, fontWeight: d.hot ? 700 : 400 }}>{d.event}</span>
            </div>
            <span style={{ ...muted, whiteSpace: "nowrap", marginLeft: 12, fontWeight: d.hot ? 700 : 400, color: d.hot ? C.gold : C.muted }}>
              {d.date}
            </span>
          </div>
        ))}
      </div>

      {/* Phase 1 — Groups */}
      <div style={card}>
        <p style={heading}>Phase 1 — Group Stage</p>
        <div style={row}>
          <div>
            <span style={label}>Correct match result (W / D / W)</span>
            <div style={{ ...muted, marginTop: 2 }}>Per match — 72 matches total</div>
          </div>
          <span style={pts}>1 pt</span>
        </div>
        <div style={row}>
          <div>
            <span style={label}>Correct final group position</span>
            <div style={{ ...muted, marginTop: 2 }}>Who finishes 1st or 2nd in each group</div>
          </div>
          <span style={pts}>1 pt</span>
        </div>
        <div style={lastRow}>
          <div>
            <span style={label}>Perfect group bonus</span>
            <div style={{ ...muted, marginTop: 2 }}>All 3 results + both standings correct</div>
          </div>
          <span style={pts}>+5 pts</span>
        </div>
      </div>

      {/* Phase 2 — Knockout */}
      <div style={card}>
        <p style={heading}>Phase 2 — Knockout Bracket</p>
        {[
          { round: "Round of 32", pts: "2 pts" },
          { round: "Round of 16", pts: "4 pts" },
          { round: "Quarterfinals", pts: "8 pts" },
          { round: "Semifinals", pts: "16 pts" },
          { round: "3rd Place Match", pts: "8 pts" },
          { round: "Final / Champion", pts: "32 pts", big: true },
        ].map((r, i, arr) => (
          <div key={r.round} style={i < arr.length - 1 ? row : lastRow}>
            <span style={{ ...label, fontWeight: r.big ? 700 : 400, color: r.big ? C.cream : C.text }}>{r.round}</span>
            <span style={{ ...pts, fontSize: r.big ? 15 : 13, color: r.big ? C.gold : C.gold }}>{r.pts}</span>
          </div>
        ))}
        <div style={{ marginTop: 12, padding: "10px 12px", background: "rgba(212,160,23,0.06)", borderRadius: 8, border: `1px solid rgba(212,160,23,0.15)` }}>
          <p style={{ ...muted, margin: 0 }}>
            <strong style={{ color: C.text }}>Bonus picks</strong> unlock per match — predict exact score (+same as base),
            clean sheet (+½ base), or penalties (+¾ base).
          </p>
        </div>
      </div>

      {/* Wildcards */}
      <div style={card}>
        <p style={heading}>🃏 Wildcard Predictions</p>
        <p style={{ ...muted, marginBottom: 12, marginTop: 0 }}>Lock in before Jun 11. Scored at end of tournament.</p>
        {[
          { label: "Tournament Winner", pts: "25 pts" },
          { label: "Exact Final Scoreline", pts: "20 pts" },
          { label: "Golden Boot — Top Scorer", pts: "15 pts" },
          { label: "Dark Horse to QF+ (outside FIFA top 20)", pts: "12 pts" },
          { label: "Total Goals in Final", pts: "Tiebreaker" },
        ].map((w, i, arr) => (
          <div key={w.label} style={i < arr.length - 1 ? row : lastRow}>
            <span style={label}>{w.label}</span>
            <span style={{ ...pts, color: w.pts === "Tiebreaker" ? C.muted : C.gold }}>{w.pts}</span>
          </div>
        ))}
      </div>

      {/* Winning */}
      <div style={{ ...card, background: "rgba(212,160,23,0.06)", border: `1px solid rgba(212,160,23,0.2)` }}>
        <p style={{ ...heading, color: C.gold }}>🏆 How to Win</p>
        <p style={{ fontSize: 13, color: C.text, lineHeight: 1.7, margin: 0 }}>
          Highest total points at the end of the Final wins the pool.
          Tiebreaker: closest guess on total goals scored in the Final.
          <br /><br />
          <span style={{ color: C.muted }}>The <strong style={{ color: C.text }}>Chaos Agent</strong> badge goes to whoever lands the biggest solo upset pick in the pool.</span>
        </p>
      </div>

      {/* Disclaimer */}
      <div style={{ ...card, background: "rgba(255,255,255,0.02)", border: `1px solid ${C.border}`, marginBottom: 0 }}>
        <p style={{ ...heading }}>⚠️ Disclaimer</p>
        <p style={{ fontSize: 11, color: C.muted, lineHeight: 1.7, margin: 0 }}>
          Chaos Pools is a free-to-play prediction game for entertainment purposes only. Drpr.ai does not collect, hold, transfer, or distribute any money or prizes. Any financial arrangements between pool participants are made entirely outside of this platform and are the sole responsibility of the pool commissioner. Scores and standings are computed automatically from live match data and are provided on a best-effort basis — they do not constitute a guarantee of accuracy. Participation is limited to 1,000 players globally.
        </p>
      </div>
    </div>
  );
}

// ── Main Pool Detail Page ────────────────────────────────────────────────────

export default function PoolDetail() {
  const { poolId } = useParams<{ poolId: string }>();
  const [, setLocation] = useLocation();
  const [activeTab, setActiveTab] = useState<"picks" | "bracket" | "leaderboard" | "how-to-play" | "settings">("picks");
  const { user } = useAuth();

  const { data: pool, isLoading: poolLoading } = useQuery<any>({
    queryKey: ["/api/pools", parseInt(poolId!)],
    queryFn: () => fetch(`/api/pools/${poolId}`, { credentials: "include" }).then((r) => r.json()),
  });

  const { data: socialCtx } = useQuery<any>({
    queryKey: ["/api/pools", parseInt(poolId!), "social-context"],
    queryFn: () => fetch(`/api/pools/${poolId}/social-context`, { credentials: "include" }).then((r) => r.json()),
    enabled: !!pool,
  });

  const { data: alerts } = useQuery<{ alerts: any[] }>({
    queryKey: ["/api/pools", parseInt(poolId!), "my-alerts"],
    queryFn: () => fetch(`/api/pools/${poolId}/my-alerts`, { credentials: "include" }).then((r) => r.json()),
    refetchInterval: 60_000,
  });

  const dismissAlertMutation = useMutation({
    mutationFn: async () => {
      await apiRequest("DELETE", `/api/pools/${poolId}/my-alerts`);
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["/api/pools", parseInt(poolId!), "my-alerts"] }),
  });

  const { toast } = useToast();

  if (poolLoading) {
    return (
      <div style={{ minHeight:"100vh", background:C.bg, display:"flex", alignItems:"center", justifyContent:"center" }}>
        <Loader2 size={32} className="animate-spin" style={{ color:C.gold }} />
      </div>
    );
  }

  if (!pool || pool.message) {
    return (
      <div style={{ minHeight:"100vh", background:C.bg, display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", gap:16 }}>
        <p style={{ color:C.muted, fontSize:14 }}>Pool not found or you don't have access.</p>
        <button onClick={() => setLocation("/pools")} style={{ padding:"10px 20px", borderRadius:8, background:C.goldDim, border:`1px solid rgba(212,160,23,0.3)`, color:C.gold, fontWeight:700, fontSize:13, cursor:"pointer", fontFamily:"inherit" }}>Back to Pools</button>
      </div>
    );
  }

  const isCreator = pool.creatorUserId === user?.id;
  const chaosAlert = alerts?.alerts?.[0];
  const phase1Locked = pool.phase1Locked ?? false;
  const phase2Unlocked = pool.phase2Unlocked ?? false;

  const tabs: { id: typeof activeTab; label: string }[] = [
    { id: "picks", label: phase1Locked ? "My Picks" : "Phase 1 — Groups" },
    { id: "bracket", label: phase2Unlocked ? "Phase 2 — Bracket" : "Bracket (Locked)" },
    { id: "leaderboard", label: "Leaderboard" },
    { id: "how-to-play", label: "❓ How to Play" },
    ...(isCreator ? [{ id: "settings" as typeof activeTab, label: "⚙ Settings" }] : []),
  ];

  return (
    <div style={{ minHeight:"100vh", background:C.bg, color:C.text, fontFamily:"'Outfit',system-ui,sans-serif", paddingBottom:40 }}>
      <style>{`*{box-sizing:border-box}@keyframes pulse{0%,100%{opacity:1}50%{opacity:0.4}}`}</style>

      {/* Header */}
      <div style={{ background:`linear-gradient(180deg,rgba(212,160,23,0.08),transparent)`, borderBottom:`1px solid rgba(212,160,23,0.15)`, padding:"16px 20px" }}>
        <div style={{ maxWidth:800, margin:"0 auto" }}>
          <div style={{ display:"flex", alignItems:"center", gap:12, marginBottom:12 }}>
            <button onClick={() => setLocation("/pools")}
              style={{ display:"flex", alignItems:"center", gap:5, fontSize:12, color:C.muted, background:"none", border:"none", cursor:"pointer", fontFamily:"inherit", padding:0 }}>
              <ArrowLeft size={14} /> Pools
            </button>
            <span style={{ color:C.border }}>›</span>
            <span style={{ fontSize:12, color:C.text }}>{pool.name}</span>
          </div>
          <div style={{ display:"flex", alignItems:"flex-start", justifyContent:"space-between", gap:12 }}>
            <div>
              <h1 style={{ fontSize:22, fontWeight:900, color:C.cream, margin:0, letterSpacing:"-0.02em" }}>{pool.name}</h1>
              {pool.description && <p style={{ fontSize:12, color:C.muted, marginTop:3 }}>{pool.description}</p>}
              <div style={{ display:"flex", alignItems:"center", gap:10, marginTop:8 }}>
                <span style={{ display:"flex", alignItems:"center", gap:5, fontSize:12, color:C.muted }}><Users size={13} /> {pool.memberCount} {pool.memberCount === 1 ? "member" : "members"}</span>
                <span style={{ fontSize:9, background: phase2Unlocked ? "rgba(74,222,128,0.1)" : C.goldDim, border:`1px solid ${phase2Unlocked ? "rgba(74,222,128,0.3)" : "rgba(212,160,23,0.3)"}`, borderRadius:6, padding:"3px 8px", color: phase2Unlocked ? C.green : C.gold, fontWeight:700, textTransform:"uppercase", letterSpacing:"0.05em" }}>
                  {phase2Unlocked ? "Phase 2" : phase1Locked ? "Phase 1 Locked" : "Phase 1"}
                </span>
              </div>
            </div>
            <button onClick={() => { navigator.clipboard.writeText(`${window.location.origin}/pools/join/${pool.inviteCode}`); toast({ title: "Invite link copied!" }); }}
              style={{ display:"flex", alignItems:"center", gap:6, padding:"9px 14px", borderRadius:9, background:C.goldDim, border:"1px solid rgba(212,160,23,0.3)", color:C.gold, fontSize:12, fontWeight:700, cursor:"pointer", fontFamily:"inherit", flexShrink:0 }}>
              <Copy size={13} /> Invite
            </button>
          </div>
        </div>
      </div>

      {/* Chaos Agent Alert */}
      {chaosAlert && (
        <div style={{ background:"rgba(212,160,23,0.12)", borderBottom:"1px solid rgba(212,160,23,0.3)", padding:"12px 20px" }}>
          <div style={{ maxWidth:800, margin:"0 auto", display:"flex", alignItems:"center", justifyContent:"space-between", gap:10 }}>
            <p style={{ fontSize:13, fontWeight:700, color:C.gold, margin:0 }}>⚡ You earned the Chaos Agent badge — biggest solo upset pick in the pool!</p>
            <button onClick={() => dismissAlertMutation.mutate()} style={{ fontSize:10, color:C.muted, background:"none", border:"none", cursor:"pointer", fontFamily:"inherit", flexShrink:0 }}>Dismiss</button>
          </div>
        </div>
      )}

      {/* Tab nav */}
      <div style={{ borderBottom:`1px solid ${C.border}`, padding:"0 20px" }}>
        <div style={{ maxWidth:800, margin:"0 auto", display:"flex", gap:2, overflowX:"auto" }}>
          {tabs.map((tab) => (
            <button key={tab.id} data-testid={`tab-${tab.id}`} onClick={() => setActiveTab(tab.id)}
              style={{ padding:"14px 16px", fontSize:12, fontWeight:700, cursor:"pointer", fontFamily:"inherit", border:"none", borderBottom:`2px solid ${activeTab === tab.id ? C.gold : "transparent"}`, background:"transparent", color: activeTab === tab.id ? C.gold : C.muted, transition:"all 0.15s", whiteSpace:"nowrap" }}>
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <div style={{ maxWidth:800, margin:"0 auto", padding:"20px 20px" }}>
        {activeTab === "picks" && (
          phase1Locked ? (
            <div style={{ background:C.cardAlt, border:`1px solid ${C.border}`, borderTop:`2px solid ${C.gold}`, borderRadius:12, padding:24, textAlign:"center" }}>
              <Lock size={24} style={{ color:C.gold, marginBottom:12 }} />
              <h3 style={{ fontSize:16, fontWeight:800, color:C.cream, marginBottom:6 }}>Phase 1 Picks Locked</h3>
              <p style={{ fontSize:12, color:C.muted, lineHeight:1.6 }}>Group stage picks are locked for the tournament. Check the leaderboard to see how you're doing as matches play out.</p>
              <button onClick={() => setActiveTab("leaderboard")} style={{ marginTop:16, padding:"10px 20px", borderRadius:8, background:C.goldDim, border:`1px solid rgba(212,160,23,0.3)`, color:C.gold, fontWeight:700, fontSize:13, cursor:"pointer", fontFamily:"inherit" }}>View Leaderboard</button>
            </div>
          ) : (
            <GroupPicksWizard poolId={parseInt(poolId!)} socialCtx={socialCtx} />
          )
        )}
        {activeTab === "bracket" && <BracketPicksWizard poolId={parseInt(poolId!)} />}
        {activeTab === "leaderboard" && <LeaderboardTab poolId={parseInt(poolId!)} />}
        {activeTab === "how-to-play" && <HowToPlayTab />}
        {activeTab === "settings" && isCreator && <CommissionerSettings pool={pool} />}
      </div>
    </div>
  );
}
